import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { presetMinutes } from '@sensika/ag-grid-kit';

import {
  UserActionStatsWidget,
  injectUserActionStatsQuery,
} from 'src/app/api/queries/client-admin.queries';
import {
  UserActionStatsRangeParams,
  UserActionStatsResponse,
} from 'src/app/api/services/client-admin-api.service';
import { Period } from 'src/app/core/models/core.models';
import {
  selectCoreDarkMode,
  selectPeriod,
  selectPeriodPreset,
} from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';
import {
  cssToken,
  dashboardAxisLabels,
  dataViewChartBase,
  dataViewTooltipTheme,
} from 'src/app/shared/utils/highcharts-theme.utils';
import {
  periodLabel,
  relativePeriod,
} from 'src/app/shared/utils/period-preset.utils';

/** The admin dashboards open on the last hour: the operational window of a wall display (WAP-1763). */
export const DEFAULT_DASHBOARD_PRESET = '1h';
export const DEFAULT_DASHBOARD_PRESET_MINUTES = 60;

const WIDGET_TITLE_KEY: Record<UserActionStatsWidget, string> = {
  hourly_active_users: 'user-action-stats-widgets.widget.hourly-active-users',
  top_active_companies: 'user-action-stats-widgets.widget.top-active-companies',
  top_action_types: 'user-action-stats-widgets.widget.top-user-action-types',
  errors_per_hour: 'user-action-stats-widgets.widget.errors-per-hour',
};

/** Empty-state text per widget when the period holds no matching rows. */
const WIDGET_EMPTY_KEY: Record<UserActionStatsWidget, string> = {
  hourly_active_users:
    'user-action-stats-widgets.empty.no-user-activity-is-logged-for-the-selected-period',
  top_active_companies:
    'user-action-stats-widgets.empty.no-user-activity-is-logged-for-the-selected-period',
  top_action_types:
    'user-action-stats-widgets.empty.no-user-activity-is-logged-for-the-selected-period',
  errors_per_hour:
    'user-action-stats-widgets.empty.no-user-activity-errors-are-logged-for-the-selected-period',
};

/** Widgets that take the whole row of the group grid. */
const FULL_WIDTH_WIDGETS: ReadonlySet<UserActionStatsWidget> = new Set([
  'hourly_active_users',
  'errors_per_hour',
]);

/** Time-series widgets, bucketed by the window length; the rankings need no buckets at all. */
const HOURLY_WIDGETS: ReadonlySet<UserActionStatsWidget> = new Set([
  'hourly_active_users',
  'errors_per_hour',
]);

const DAY_GAP = '+1DAY';

/** Bucket of the time-series widgets by window length: a one-hour window at an hourly gap is a single invisible point. */
function bucketFor(spanSeconds: number): { gap: string; labelKey: string } {
  if (spanSeconds <= 3 * 3600) {
    return { gap: '+5MINUTE', labelKey: 'per-5-minutes' };
  }
  if (spanSeconds <= 12 * 3600) {
    return { gap: '+15MINUTE', labelKey: 'per-15-minutes' };
  }
  if (spanSeconds <= 7 * 86400) {
    return { gap: '+1HOUR', labelKey: 'per-hour' };
  }
  return { gap: DAY_GAP, labelKey: 'per-day' };
}

/**
 * User activity widget group of the admin dashboards (WAP-1762): the
 * user-action aggregations over the page's period (the header date-range
 * picker, WAP-1763), drawn as one chart per widget on the shared data-view
 * recipe and Highcharts theme, sized for the DevOps wall displays: hourly
 * active users and errors per hour as full-width lines, the top 20 companies
 * and the top 20 action types ranked over the period as horizontal bars. The
 * host names the widgets to show and their order. A now-relative preset is
 * re-evaluated on Refresh, so "last hour" stays the last hour; every chart
 * re-derives when the theme flips.
 */
@Component({
  selector: 'app-user-action-stats-widgets',
  templateUrl: './user-action-stats-widgets.component.html',
  styleUrl: './user-action-stats-widgets.component.scss',
  standalone: false,
})
export class UserActionStatsWidgetsComponent {
  /** The widgets to show, in order. */
  readonly widgets = input.required<UserActionStatsWidget[]>();

  private readonly store = inject(Store<AppState>);
  private readonly translateService = inject(TranslateService);
  private readonly queryClient = inject(QueryClient);

  protected readonly Highcharts: typeof Highcharts = Highcharts;

  private readonly period = toSignal(this.store.select(selectPeriod), {
    initialValue: null as Period | null,
  });
  private readonly preset = toSignal(this.store.select(selectPeriodPreset), {
    initialValue: null as string | null,
  });
  private readonly darkMode = toSignal(this.store.select(selectCoreDarkMode));
  /** Advances on Refresh so a now-relative window is re-evaluated. */
  private readonly clock = signal(Date.now());

  /** The page period, slid to now for a now-relative preset; the default window until the store has one. */
  private readonly window = computed<Period>(() => {
    const minutes = presetMinutes(this.preset());
    const now = this.clock();
    if (minutes !== null) {
      return relativePeriod(minutes, now);
    }
    return (
      this.period() ?? relativePeriod(DEFAULT_DASHBOARD_PRESET_MINUTES, now)
    );
  });

  /** The active period, shown in brackets next to every widget title. */
  protected readonly periodLabel = computed(() =>
    periodLabel(this.window(), this.preset(), this.translateService),
  );

  private readonly bucket = computed(() => {
    const { startTimestamp, endTimestamp } = this.window();
    return bucketFor(endTimestamp - startTimestamp);
  });

  /** The time-series bucket next to the period, "per 5 minutes" for the last hour. */
  protected readonly bucketLabel = computed(() =>
    this.translateService.instant(
      `user-action-stats-widgets.bucket.${this.bucket().labelKey}`,
    ),
  );

  /** The widget's range while it is shown; the hourly widgets bucket per hour, the rankings per day. */
  private rangeFor(widget: UserActionStatsWidget) {
    return computed((): UserActionStatsRangeParams | null => {
      if (!this.widgets().includes(widget)) {
        return null;
      }
      const { startTimestamp, endTimestamp } = this.window();
      return {
        from: startTimestamp,
        to: endTimestamp,
        gap: HOURLY_WIDGETS.has(widget) ? this.bucket().gap : DAY_GAP,
      };
    });
  }

  private readonly queries = {
    hourly_active_users: injectUserActionStatsQuery(
      'hourly_active_users',
      this.rangeFor('hourly_active_users'),
    ),
    top_active_companies: injectUserActionStatsQuery(
      'top_active_companies',
      this.rangeFor('top_active_companies'),
    ),
    top_action_types: injectUserActionStatsQuery(
      'top_action_types',
      this.rangeFor('top_action_types'),
    ),
    errors_per_hour: injectUserActionStatsQuery(
      'errors_per_hour',
      this.rangeFor('errors_per_hour'),
    ),
  };

  protected readonly loading = computed(() =>
    this.widgets().some((widget) => this.queries[widget].isFetching()),
  );

  /** One entry per shown widget: its query state and the chart options derived from its data. */
  protected readonly views = computed(() =>
    this.widgets().map((widget) => {
      const query = this.queries[widget];
      const response = query.data();
      // An answered period with only zeros is an empty state, not a flat chart.
      const empty =
        !!response &&
        response.series.every((s) => s.data.every((value) => value === 0));
      return {
        key: widget,
        titleKey: WIDGET_TITLE_KEY[widget],
        emptyKey: WIDGET_EMPTY_KEY[widget],
        fullWidth: FULL_WIDTH_WIDGETS.has(widget),
        timeSeries: HOURLY_WIDGETS.has(widget),
        pending: query.isPending(),
        refreshing: query.isFetching() && !!response,
        failed: query.isError(),
        empty,
        options: empty ? null : this.optionsFor(widget, response),
      };
    }),
  );

  protected refresh(): void {
    this.clock.set(Date.now());
    this.queryClient.invalidateQueries({
      queryKey: ['client_admin', 'user_action_stats'],
    });
  }

  private optionsFor(
    widget: UserActionStatsWidget,
    response: UserActionStatsResponse | undefined,
  ): Highcharts.Options | null {
    // * Series fills are baked to literals via getComputedStyle (Highcharts
    // * applies them as SVG presentation attributes, where var() cannot work),
    // * so the options must re-derive when the theme flips.
    this.darkMode();
    if (!response) {
      return null;
    }
    switch (widget) {
      case 'hourly_active_users':
        return this.buildHourlyLineChart(
          response,
          '--color-feedback-info-solid',
        );
      case 'top_active_companies':
        return this.buildRankedBarChart(
          response.series.map((s) => ({
            name: s.name,
            total: s.data.reduce((sum, value) => sum + value, 0),
          })),
        );
      case 'top_action_types':
        return this.buildRankedBarChart(
          response.categories.map((name, index) => ({
            name,
            total: response.series[0]?.data[index] ?? 0,
          })),
        );
      default:
        return this.buildHourlyLineChart(
          response,
          '--color-feedback-danger-solid',
        );
    }
  }

  /** Shared chrome of the charts: the data-view base at dashboard height, themed axes and tooltip, no built-in title. */
  private chartBase(categories: string[]): Highcharts.Options {
    const base = dataViewChartBase();
    return {
      ...base,
      chart: { ...base.chart, height: 380 },
      plotOptions: {
        ...base.plotOptions,
        column: { ...base.plotOptions?.column, cursor: undefined },
      },
      xAxis: {
        categories,
        labels: dashboardAxisLabels(),
        lineColor: cssToken('--color-border-default'),
        tickLength: 0,
      },
      yAxis: {
        title: { text: undefined },
        labels: dashboardAxisLabels(),
        gridLineColor: cssToken('--color-border-default'),
        allowDecimals: false,
      },
      tooltip: { ...dataViewTooltipTheme(), shared: true },
    };
  }

  /**
   * A time series over the period: minute or hour buckets whose categories
   * are `YYYY-MM-DDTHH:MM` in UTC, drawn on a datetime axis in local time so
   * a week reads as 168 points with day ticks. Markers show while the series
   * is short enough for a point to matter.
   */
  private buildHourlyLineChart(
    response: UserActionStatsResponse,
    colorToken: string,
  ): Highcharts.Options {
    const base = this.chartBase([]);
    return {
      ...base,
      time: { useUTC: false },
      xAxis: {
        ...base.xAxis,
        type: 'datetime',
        categories: undefined,
      },
      tooltip: { ...base.tooltip, xDateFormat: '%d %b %H:%M' },
      series: response.series.map(
        (s): Highcharts.SeriesLineOptions => ({
          type: 'line',
          name: s.name,
          data: s.data.map((value, index) => [
            Date.parse(`${response.categories[index]}:00Z`),
            value,
          ]),
          color: cssToken(colorToken),
          marker: { enabled: s.data.length <= 48, radius: 3, lineWidth: 0 },
          lineWidth: 2,
        }),
      ),
    };
  }

  /** Entries ranked by their total over the whole period, one horizontal bar each (companies, action types). */
  private buildRankedBarChart(
    entries: Array<{ name: string; total: number }>,
  ): Highcharts.Options {
    const ranked = [...entries].sort((a, b) => b.total - a.total);
    const base = this.chartBase(ranked.map((entry) => entry.name));
    return {
      ...base,
      chart: {
        ...base.chart,
        height: Math.max(380, ranked.length * 30 + 60),
      },
      tooltip: { ...base.tooltip, shared: false },
      series: [
        {
          type: 'bar',
          name: this.translateService.instant(
            'user-action-stats-widgets.series.actions',
          ),
          data: ranked.map((entry) => entry.total),
          color: cssToken('--color-feedback-info-solid'),
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '14px',
              fontWeight: 'normal',
              color: 'var(--color-text-secondary)',
              textOutline: 'none',
            },
          },
        },
      ],
    };
  }
}
